import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaFlask, FaUniversity, FaBuilding, FaCheck, FaTimes, FaClock, FaUser, FaCalendarAlt } from 'react-icons/fa';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import withRoleGuard from '../hoc/withRoleGuard';
import { useAuth } from '../context/AuthContext';
import useCachedApi, { CacheKeys, CacheTTL } from '../hooks/useCachedApi';
import { useStudentTracking } from '../hooks/useStudentTracking';
import { useEditTracking } from '../hooks/useEditTracking';

const Lab = () => {
  const { token, user } = useAuth();
  const { cachedGet, invalidate } = useCachedApi();
  const { trackPageView, trackButtonClick, trackDataOperation, trackError } = useStudentTracking();
  const { trackStatusChange } = useEditTracking();
  const [labRequests, setLabRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    trackPageView('lab', { userId: user?.studentId || user?.id, userName: user?.name });
    fetchLabRequests();
  }, [user?.id, user?.studentId, token]);

  const fetchLabRequests = async (forceRefresh = false) => {
    try {
      if (!token) return;
      const userId = user?.id || user?.studentId;
      const data = await cachedGet('/api/lab-requests/student', CacheKeys.STUDENT_LAB_REQUESTS(userId), CacheTTL.SHORT, { forceRefresh });
      setLabRequests(Array.isArray(data) ? data : []);
      
      trackDataOperation('fetch', 'lab_requests', 'all', {
        requestCount: Array.isArray(data) ? data.length : 0
      });
    } catch (error) {
      console.error('Error fetching lab requests:', error);
      
      // Track error
      trackError('lab_requests_fetch_error', error.message, {
        statusCode: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'approved': return 'text-green-600 bg-green-100 border-green-200';
      case 'rejected': return 'text-red-600 bg-red-100 border-red-200';
      case 'completed': return 'text-[#0077B6] bg-[rgba(0,119,182,0.1)] border-[rgba(0,119,182,0.2)]';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FaClock className="h-4 w-4" />;
      case 'approved': return <FaCheck className="h-4 w-4" />;
      case 'rejected': return <FaTimes className="h-4 w-4" />;
      case 'completed': return <FaCheck className="h-4 w-4" />;
      default: return <FaFlask className="h-4 w-4" />;
    }
  };

  const getLabTypeIcon = (labType) => {
    return labType === 'university' ? 
      <FaUniversity className="h-5 w-5 text-blue-600" /> : 
      <FaBuilding className="h-5 w-5 text-green-600" />;
  };

  const filteredRequests = labRequests.filter(request => {
    if (filter === 'all') return true;
    return request.status === filter;
  });

  const getStatusCounts = () => {
    return {
      all: labRequests.length,
      pending: labRequests.filter(r => r.status === 'pending').length,
      approved: labRequests.filter(r => r.status === 'approved').length,
      rejected: labRequests.filter(r => r.status === 'rejected').length,
      completed: labRequests.filter(r => r.status === 'completed').length,
    };
  };

  const statusCounts = getStatusCounts();

  const formatDate = (dateValue) => {
    try {
      let date;
      
      // Handle different date formats from Firestore
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        // Firestore timestamp
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        // Firestore timestamp object
        date = new Date(dateValue._seconds * 1000);
      } else {
        console.warn('Invalid date format:', dateValue);
        return null;
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date after parsing:', dateValue);
        return null;
      }
      
      return date;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  const formatDateString = (dateValue) => {
    const date = formatDate(dateValue);
    if (!date) return 'Date not available';
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-24 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 md:p-8"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
                <div className="flex items-center">
                  <FaFlask className="h-8 w-8 text-[#0077B6] mr-3 sm:mr-4" />
                  <h1 className="text-2xl md:text-3xl font-bold text-[#0077B6]">Lab Requests</h1>
                </div>
                <div className="text-sm text-gray-500">
                  Total Requests: {labRequests.length}
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
                {[
                  { key: 'all', label: 'All', count: statusCounts.all },
                  { key: 'pending', label: 'Pending', count: statusCounts.pending },
                  { key: 'approved', label: 'Approved', count: statusCounts.approved },
                  { key: 'completed', label: 'Completed', count: statusCounts.completed },
                  { key: 'rejected', label: 'Rejected', count: statusCounts.rejected },
                ].map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setFilter(key);
                      trackButtonClick('lab_filter_change', { 
                        filter: key,
                        count: count
                      });
                    }}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === key
                        ? 'bg-[#0077B6] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-[rgba(0,119,182,0.1)] hover:text-[#0077B6]'
                    }`}
                  >
                    {label} ({count})
                  </button>
                ))}
              </div>

              {/* Lab Requests List */}
              {filteredRequests.length === 0 ? (
                <div className="text-center py-10 sm:py-12">
                  <FaFlask className="h-12 sm:h-16 w-12 sm:w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-500 mb-2">
                    {filter === 'all' ? 'No lab requests found' : `No ${filter} requests found`}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Lab requests will appear here once you submit them from patient profiles.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {filteredRequests.map((request) => (
                    <motion.div
                      key={request.id || request._id || Math.random()}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                        <div className="flex items-center">
                          {getLabTypeIcon(request.labType || 'university')}
                          <div className="ml-3">
                            <h3 className="text-base sm:text-lg font-semibold text-[#333333]">
                              {request.labType === 'university' ? 'University Lab' : request.labType === 'outside' ? 'Outside Lab' : 'Lab Request'}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600">Request ID: {request.id ? request.id.slice(-8) : request._id ? request._id.slice(-8) : 'N/A'}</p>
                          </div>
                        </div>
                        <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(request.status)}`}>
                          <div className="flex items-center">
                            {getStatusIcon(request.status)}
                            <span className="ml-1">{request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : 'Unknown'}</span>
                          </div>
                        </span>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <FaUser className="h-4 w-4 mr-2 text-[#0077B6]" />
                          <span className="font-medium">Patient:</span>
                          <span className="ml-1">{request.patientName || 'Unknown Patient'}</span>
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <FaCalendarAlt className="h-4 w-4 mr-2 text-[#0077B6]" />
                          <span className="font-medium">Submitted:</span>
                          <span className="ml-1">{formatDateString(request.submitDate)}</span>
                        </div>
                      </div>

                      {request.formData && (
                        <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            Form Type: {request.formData.formType ? request.formData.formType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                          </p>
                          {request.formData.tooth && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Tooth:</span> {request.formData.tooth}
                            </p>
                          )}
                          {request.formData.design && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Design:</span> {request.formData.design}
                            </p>
                          )}
                          {request.formData.material && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Material:</span> {request.formData.material}
                            </p>
                          )}
                          {request.formData.pontic && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Pontic:</span> {request.formData.pontic}
                            </p>
                          )}
                          {request.formData.color && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Color:</span> {request.formData.color}
                            </p>
                          )}
                          {request.formData.laboratorySteps && (
                            <div className="mt-2">
                              <p className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Laboratory Steps:</p>
                              <ul className="text-xs sm:text-sm text-gray-600 list-disc list-inside space-y-1">
                                {request.formData.laboratorySteps.mountedDiagnosticCasts && <li>Mounted Diagnostic Casts</li>}
                                {request.formData.laboratorySteps.diagnosticWaxUp && <li>Diagnostic Wax up</li>}
                                {request.formData.laboratorySteps.provisionalRestoration && <li>Provisional Restoration</li>}
                                {request.formData.laboratorySteps.studyCastsAndCustomTrays && <li>Study casts and Custom trays</li>}
                                {request.formData.laboratorySteps.masterCastsAndRecordBlocks && <li>Master casts and record blocks</li>}
                                {request.formData.laboratorySteps.settingOfTeeth && <li>Setting of teeth on mounted master casts</li>}
                                {request.formData.laboratorySteps.finalProcessedPolishedDenture && <li>Final processed polished denture</li>}
                                {request.formData.laboratorySteps.masterCastsAndMetalFramework && <li>Master casts and metal framework</li>}
                                {request.formData.laboratorySteps.rpdTryIn && <li>RPD try-in</li>}
                              </ul>
                            </div>
                          )}
                          {request.formData.tryInOk !== undefined && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">
                              <span className="font-medium">Try In Status:</span> {request.formData.tryInOk ? 'OK' : request.formData.problemWithTryIn ? 'Problem with try in' : 'Not specified'}
                            </p>
                          )}
                          {request.formData.remarks && (
                            <p className="text-xs sm:text-sm text-gray-600 mt-2">
                              <span className="font-medium">Remarks:</span> {request.formData.remarks}
                            </p>
                          )}
                          {request.formData.notes && (
                            <p className="text-xs sm:text-sm text-gray-600 mt-2">
                              <span className="font-medium">Notes:</span> {request.formData.notes}
                            </p>
                          )}
                        </div>
                      )}

                      {request.notes && !request.formData && (
                        <div className="mb-4">
                          <p className="text-xs sm:text-sm text-gray-600">
                            <span className="font-medium">Notes:</span> {request.notes}
                          </p>
                        </div>
                      )}

                      {request.responseNotes && (
                        <div className="bg-[rgba(0,119,182,0.05)] p-2.5 sm:p-3 rounded-lg border border-[rgba(0,119,182,0.1)]">
                          <p className="text-xs sm:text-sm text-gray-600">
                            <span className="font-medium">Response:</span> {request.responseNotes}
                          </p>
                          {request.responseDate && (
                            <p className="text-[11px] sm:text-xs text-gray-500 mt-1">
                              Responded on: {formatDateString(request.responseDate)}
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withRoleGuard(['student'])(Lab);
